import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar, BookOpen, Home, Building2, LogOut, Camera, Image,
  Menu, Sun, Moon, Globe, Shield, BarChart3,
  Megaphone, Star, ChevronRight, X,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { cn, initials } from "@/lib/utils";

const PORTAL_NAV = [
  { to: "/portal",          label: "Overview",     icon: Home,      end: true },
  { to: "/portal/book",     label: "Book a hall",  icon: BookOpen },
  { to: "/portal/bookings", label: "My bookings",  icon: Calendar },
  { to: "/portal/events",   label: "Host event",   icon: Megaphone },
  { to: "/portal/calendar", label: "Calendar",     icon: Calendar },
  { to: "/events",          label: "Public events",icon: Globe },
  { to: "/my-events",       label: "My events",    icon: Star },
];
const ADMIN_NAV = [
  { to: "/admin",               label: "Dashboard",    icon: BarChart3,  end: true },
  { to: "/admin/bookings",      label: "Bookings",     icon: BookOpen },
  { to: "/admin/gallery",       label: "Gallery review",icon: Image },
  { to: "/admin/halls",         label: "Hall config",  icon: Building2,  superadmin: true },
  { to: "/admin/photographers", label: "Photographers",icon: Camera,     superadmin: true },
  { to: "/portal",              label: "Book / host",  icon: Megaphone },
];
const PORTAL_TABS = [
  { to: "/portal",          label: "Home",    icon: Home,      end: true },
  { to: "/portal/book",     label: "Book",    icon: BookOpen },
  { to: "/portal/bookings", label: "Bookings",icon: Calendar },
  { to: "/portal/events",   label: "Host",    icon: Megaphone },
  { to: "/events",          label: "Events",  icon: Globe },
];
const ADMIN_TABS = [
  { to: "/admin",           label: "Dashboard",icon: BarChart3, end: true },
  { to: "/admin/bookings",  label: "Bookings", icon: BookOpen },
  { to: "/admin/gallery",   label: "Gallery",  icon: Image },
  { to: "/admin/halls",     label: "Halls",    icon: Building2, superadmin: true },
  { to: "/admin/photographers", label: "Photos", icon: Camera, superadmin: true },
  { to: "/portal",          label: "Portal",   icon: Megaphone },
];

export default function Shell({ role = "portal", children, title, subtitle, actions }) {
  const { user, isSuperAdmin, isIncharge, toggleTheme, theme, logout } = useAuth();
  // Only superadmins and hall incharges may see/reach the admin panel.
  const canAccessAdmin = isSuperAdmin || isIncharge || user?.role === "incharge";
  const loc = useLocation();
  const nav = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const sidebarItems = (role === "admin" ? ADMIN_NAV : PORTAL_NAV).filter(i => !i.superadmin || isSuperAdmin);
  const tabItems     = (role === "admin" ? ADMIN_TABS : PORTAL_TABS).filter(i => !i.superadmin || isSuperAdmin);
  const isActive     = item => item.end ? loc.pathname === item.to : loc.pathname.startsWith(item.to);
  const doLogout     = () => { logout(); nav("/login", { replace: true }); };
  const name         = user?.email?.split("@")[0] || "";
  const roleLabel    = { superadmin: "Super Admin", incharge: "Hall Incharge", user: "Staff" }[user?.role] || "Staff";

  useEffect(() => { setDrawerOpen(false); }, [loc.pathname]);
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  const NavLink = ({ item }) => {
    const active = isActive(item);
    const Icon   = item.icon;
    return (
      <Link to={item.to} className={cn(
        "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 group relative",
        active
          ? "bg-blue-600 text-white shadow-sm shadow-blue-600/25"
          : "text-muted-foreground hover:text-foreground hover:bg-muted"
      )}>
        <Icon className="h-4 w-4 shrink-0" />
        <span className="flex-1 truncate">{item.label}</span>
        {active && <ChevronRight className="h-3 w-3 opacity-60 shrink-0" />}
      </Link>
    );
  };

  const UserBlock = () => (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-muted/60 border border-border/50">
      <div className="w-8 h-8 rounded-xl bg-blue-600/12 border border-blue-600/20 flex items-center justify-center text-xs font-bold text-blue-600 dark:text-blue-400 shrink-0">
        {initials(user?.email)}
      </div>
      <div className="min-w-0">
        <div className="text-xs font-semibold truncate text-foreground">{name}</div>
        <div className="text-[10px] text-muted-foreground">{roleLabel}</div>
      </div>
    </div>
  );

  const Footer = () => (
    <div className="space-y-0.5">
      {role === "portal" && canAccessAdmin && (
        <Link to="/admin" className="flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
          <Shield className="h-3.5 w-3.5" />Admin panel
        </Link>
      )}
      <button onClick={toggleTheme}
        className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors text-left">
        {theme === "dark" ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
        {theme === "dark" ? "Light mode" : "Dark mode"}
      </button>
      <button onClick={doLogout}
        className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors text-left">
        <LogOut className="h-3.5 w-3.5" />Sign out
      </button>
    </div>
  );

  return (
    <div className="min-h-dvh flex items-start">
      {/* ── Desktop sidebar ──────────────────────────────── */}
      <aside className="hidden lg:flex w-56 xl:w-60 shrink-0 flex-col glass-heavy border-r border-border sticky top-0 h-dvh">
        {/* Logo */}
        <div className="px-4 py-5 border-b border-border/50">
          <Link to={role === "admin" ? "/admin" : "/portal"} className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-blue-600 flex items-center justify-center shadow-md shadow-blue-600/30 shrink-0">
              <Building2 className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="text-sm font-bold text-foreground tracking-tight">BMSIT&M</div>
              <div className="text-[10px] text-muted-foreground">
                {role === "admin" ? (isSuperAdmin ? "Super Admin" : "Hall Incharge") : "Events Platform"}
              </div>
            </div>
          </Link>
        </div>

        {/* Nav section */}
        <div className="px-3 py-4 flex-1 overflow-y-auto space-y-0.5">
          <div className="label-xs px-2 mb-2">
            {role === "admin" ? "Administration" : "Portal"}
          </div>
          {sidebarItems.map(item => <NavLink key={item.to} item={item} />)}
        </div>

        {/* Footer */}
        <div className="px-3 py-4 border-t border-border/50 space-y-3">
          <Footer />
          <UserBlock />
        </div>
      </aside>

      {/* ── Mobile drawer ─────────────────────────────── */}
      <AnimatePresence>
        {drawerOpen && <>
          <motion.div key="ov"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-40 bg-black/50 lg:hidden"
            onClick={() => setDrawerOpen(false)} />
          <motion.aside key="dr"
            initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 320 }}
            className="fixed left-0 top-0 bottom-0 z-50 w-64 flex flex-col glass-heavy border-r border-border lg:hidden"
            style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}>
            <div className="px-4 py-4 border-b border-border/50 flex items-center justify-between">
              <Link to={role === "admin" ? "/admin" : "/portal"} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-blue-600 flex items-center justify-center">
                  <Building2 className="h-4 w-4 text-white" />
                </div>
                <div>
                  <div className="text-sm font-bold">BMSIT&M</div>
                  <div className="text-[10px] text-muted-foreground">Events Platform</div>
                </div>
              </Link>
              <button onClick={() => setDrawerOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-xl bg-muted text-muted-foreground">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="px-3 py-4 flex-1 overflow-y-auto space-y-0.5">
              <div className="label-xs px-2 mb-2">{role === "admin" ? "Administration" : "Portal"}</div>
              {sidebarItems.map(item => <NavLink key={item.to} item={item} />)}
            </div>
            <div className="px-3 py-4 border-t border-border/50 space-y-3">
              <Footer />
              <UserBlock />
            </div>
          </motion.aside>
        </>}
      </AnimatePresence>

      {/* ── Main content ───────────────────────────────── */}
      <div className="flex-1 min-w-0 flex flex-col min-h-dvh">
        {/* Header */}
        <header className="sticky top-0 z-30 header-glass shrink-0" style={{ paddingTop: "env(safe-area-inset-top,0px)" }}>
          <div className="h-14 flex items-center gap-3 px-4">
            <button onClick={() => setDrawerOpen(true)}
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-xl hover:bg-muted transition-colors text-muted-foreground shrink-0">
              <Menu className="h-5 w-5" />
            </button>
            <div className="flex-1 min-w-0">
              {title    && <h1 className="text-sm font-bold text-foreground truncate tracking-tight">{title}</h1>}
              {subtitle && <p className="text-[11px] text-muted-foreground hidden sm:block truncate">{subtitle}</p>}
            </div>
            {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
          </div>
        </header>

        {/* Body — scrolls with the document (no nested scroll trap) */}
        <main className="flex-1">
          <div className="p-4 sm:p-5 lg:p-6 pb-6 lg:pb-10 mb-tab-bar lg:mb-0 max-w-[1280px]">
            {role === "portal" && canAccessAdmin && (
              <div className="mb-4 flex items-center gap-2 px-3.5 py-2.5 rounded-xl bg-blue-50 dark:bg-blue-950/25 border border-blue-200/60 dark:border-blue-900/50 text-xs text-blue-700 dark:text-blue-300">
                <Shield className="h-3.5 w-3.5 shrink-0" />
                <span className="flex-1">
                  You're viewing the shared booking &amp; hosting portal as <span className="font-semibold">{roleLabel}</span>.
                </span>
                <Link to="/admin" className="font-semibold hover:underline shrink-0 flex items-center gap-1">
                  Admin panel <ChevronRight className="h-3 w-3" />
                </Link>
              </div>
            )}
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              {children}
            </motion.div>
          </div>
        </main>
      </div>

      {/* ── Mobile bottom tab bar ─────────────────────── */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 tab-bar-glass"
        style={{ paddingBottom: "env(safe-area-inset-bottom,0px)" }}>
        <div className="flex h-[60px]">
          {tabItems.map(item => {
            const active = isActive(item);
            const Icon   = item.icon;
            return (
              <Link key={item.to} to={item.to}
                className={cn("flex-1 flex flex-col items-center justify-center gap-0.5 relative",
                  active ? "text-blue-600" : "text-muted-foreground")}>
                {active && (
                  <motion.span layoutId={`tab-${role}`}
                    className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-[2.5px] rounded-b-full bg-blue-600"
                    transition={{ type: "spring", stiffness: 400, damping: 35 }} />
                )}
                <Icon className={cn("h-5 w-5 mt-1", active && "fill-blue-600/10")} />
                <span className={cn("text-[9px] font-semibold leading-none", !active && "text-muted-foreground/80")}>
                  {item.label}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
